import { H5Channel, ProductType } from '@/AppGlobal';
import {
  EkycNextOperation,
  EkycSource,
  H5Entrance,
  H5ProcessState,
  UserInfoType,
} from '@/interface/KKC';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// 授信提示类型
export enum TipsType {
  None = 'none',
  TopBar = 'topBar', // 顶部提示
  Bubble = 'bubble', // 气泡提示
}

type commonUrlType = {
  url: string | null;
  images: string[];
  title?: string;
  canDownload?: boolean;
};

type EkycParamsType = any;

interface CommonState {
  h5Entrance: H5Entrance;
  h5ProcessState: H5ProcessState;
  userInfo: UserInfoType;
  commonUrl: commonUrlType;
  ekycOption: {
    nextOperation: EkycNextOperation;
    source: EkycSource;
    tranid?: string; // 是否需要检查交易id，并放进交易id
    params?: EkycParamsType; // 传递可能用上的任何参数
  };
  commonApiErrorTitle?: string; // 通用接口错误信息
  commonApiErrorMsg?: string; // 通用接口错误信息
  h5InviteCode?: string; // 邀请码
  h5ProductType?: ProductType; // 浏览器版本H5的所属产品类型
  h5Channel?: H5Channel; // 浏览器版本H5的所属渠道
}

const initialState: CommonState = {
  h5Entrance: H5Entrance.Main,
  h5ProcessState: H5ProcessState.Credit,
  userInfo: {
    userId: '',
    userName: '',
    mobilePhone: '',
    encryptPhone: '',
  },
  commonUrl: {
    url: null,
    title: undefined,
    images: [],
    canDownload: false,
  },
  ekycOption: {
    nextOperation: EkycNextOperation.CheckCreditState,
    source: EkycSource.Credit,
    tranid: undefined,
    params: undefined,
  },
  commonApiErrorMsg: '',
  h5InviteCode: '',
  h5Channel: undefined,
};

const h5AppSlice = createSlice({
  name: 'h5Store',
  initialState,
  reducers: {
    setH5Entrance(state: any, action: PayloadAction<{ h5Entrance: H5Entrance }>) {
      state.h5Entrance = action.payload.h5Entrance;
    },
    setH5ProcessState(state: any, action: PayloadAction<{ h5ProcessState: H5ProcessState }>) {
      state.h5ProcessState = action.payload.h5ProcessState;
    },
    setUserInfo(state: CommonState, action: PayloadAction<{ userInfo: UserInfoType }>) {
      state.userInfo = action.payload.userInfo;
    },
    setCommonUrl(state: CommonState, action: PayloadAction<{ commonUrl: commonUrlType }>) {
      state.commonUrl = action.payload.commonUrl;
    },
    clearCommonUrl(state: CommonState) {
      state.commonUrl = {
        url: null,
        title: undefined,
        images: [],
        canDownload: false,
      };
    },
    setEkycOption(
      state: CommonState,
      action: PayloadAction<{
        nextOperation: EkycNextOperation;
        source: EkycSource;
        params?: EkycParamsType;
      }>
    ) {
      state.ekycOption.nextOperation = action.payload.nextOperation;
      state.ekycOption.source = action.payload.source;
      state.ekycOption.params = action.payload.params;
    },
    setEkycTranid(state: CommonState, action: PayloadAction<string>) {
      state.ekycOption.tranid = action.payload;
    },
    resetEkycOption(state: CommonState) {
      state.ekycOption.nextOperation = EkycNextOperation.CheckCreditState;
      state.ekycOption.source = EkycSource.Credit;
      state.ekycOption.tranid = undefined;
    },
    setCommonApiErrorMsg(state: CommonState, action: PayloadAction<string>) {
      state.commonApiErrorTitle = '';
      state.commonApiErrorMsg = action.payload;
    },
    setCommonApiErrorEntity(
      state: CommonState,
      action: PayloadAction<{
        title: string;
        message: string;
      }>
    ) {
      state.commonApiErrorTitle = action.payload.title;
      state.commonApiErrorMsg = action.payload.message;
    },
    setH5InviteCode(state: CommonState, action: PayloadAction<string>) {
      state.h5InviteCode = action.payload;
    },
    setH5ProductType(state: CommonState, action: PayloadAction<ProductType>) {
      state.h5ProductType = action.payload;
    },
    setH5Channel(state: CommonState, action: PayloadAction<H5Channel | undefined>) {
      state.h5Channel = action.payload;
    },
  },
});

const {
  setH5Entrance,
  setH5ProcessState,
  setUserInfo,
  setCommonUrl,
  clearCommonUrl,
  setEkycOption,
  setEkycTranid,
  resetEkycOption,
  setCommonApiErrorMsg,
  setCommonApiErrorEntity,
  setH5InviteCode,
  setH5ProductType,
  setH5Channel,
} = h5AppSlice.actions;

export {
  clearCommonUrl,
  resetEkycOption,
  setCommonApiErrorEntity,
  setCommonApiErrorMsg,
  setCommonUrl,
  setEkycOption,
  setEkycTranid,
  setH5Channel,
  setH5Entrance,
  setH5InviteCode,
  setH5ProcessState,
  setH5ProductType,
  setUserInfo,
};

export default h5AppSlice.reducer;
